import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-ink-muted">
      <span
        aria-hidden
        className="h-4 w-4 animate-spin rounded-full border-2 border-ink-subtle border-r-transparent"
      />
      {label}
    </span>
  );
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-md border border-line bg-surface shadow-card', className)}
      {...props}
    />
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-ink-muted">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-line-strong bg-surface px-6 py-12 text-center">
      <p className="font-medium text-ink">{title}</p>
      {description && <p className="max-w-sm text-sm text-ink-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** New (`phase-0-foundation.md` task 3) — an inline forbidden-state block
 * for use WITHIN a page (e.g. a gated section or tab's content), distinct
 * from the full-route `ForbiddenPage` in `app/pages.tsx` (which already
 * composes `PageHeader` + `EmptyState` for a whole-page 403). Not wired
 * into any page in Phase 0. */
export function ForbiddenState({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-card border border-line bg-canvas/50 px-6 py-8 text-center">
      <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </div>
      <p className="font-medium text-ink">{title}</p>
      {description && <p className="max-w-sm text-sm text-ink-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** `tone: 'accent'` — DESIGN.md's ochre "attention, not failure" tone, for
 * a rate-limited/cooldown state as distinct from a hard error (alert). */
export function ErrorNote({ children, tone = 'alert' }: { children: ReactNode; tone?: 'alert' | 'accent' }) {
  return (
    <div
      role="alert"
      className={cn(
        'rounded border px-3 py-2 text-sm',
        tone === 'accent' ? 'border-accent/30 bg-accent-soft text-accent-fg' : 'border-alert/30 bg-alert-soft text-alert',
      )}
    >
      {children}
    </div>
  );
}

export function InlineStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 text-lg font-semibold tabular text-ink">{value}</dd>
    </div>
  );
}
